import React, { ReactElement, FC, useEffect, useState, useRef } from 'react'
import { throttle } from 'lodash'
import { useTranslation } from 'react-i18next'
import { Layout, Menu, Typography, Button } from 'antd'
import { PlusSquareOutlined, MinusSquareOutlined } from '@ant-design/icons'
import { CombinedError } from 'urql'
import { EventMenuItem } from './EventMenuItem/EventMenuItem'
import { s } from './style'
import { LoadError } from '../../../Components/UI'
import { BRIEF_BUILDER } from '../../../Constants'
import { SideBarEventFragment } from './SideBar.generated-types'

const { Header } = Layout
const { Paragraph, Text, Link } = Typography

interface Props {
  activeLoading: boolean
  activeError: CombinedError
  activeEvents: SideBarEventFragment[]
  inactiveLoading: boolean
  inactiveError: CombinedError
  inactiveEvents: SideBarEventFragment[]
  setSelectedEventListItem: (selected: SideBarEventFragment) => void
  selectedEventListItem: SideBarEventFragment
}

export const SideBar: FC<Props> = ({
  activeLoading,
  activeError,
  activeEvents,
  inactiveLoading,
  inactiveError,
  inactiveEvents,
  setSelectedEventListItem,
  selectedEventListItem,
}): ReactElement => {
  const headerRef = useRef(null)
  const [showHeaderBorder, setShowHeaderBorder] = useState(false)
  const [showArchive, setShowArchive] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    const handleScroll = throttle((e) => {
      if (e.target.scrollTop !== 0) {
        setShowHeaderBorder(true)
      } else {
        setShowHeaderBorder(false)
      }
    }, 50)

    if (headerRef?.current?.addEventListener) {
      headerRef.current.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (headerRef?.current?.removeEventListener) {
        headerRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [headerRef?.current])

  useEffect(() => {
    if (activeEvents?.length === 0) {
      setShowArchive(true)
    }
  }, [activeEvents])

  const handleToggleShowArchive = (): void => setShowArchive(!showArchive)

  return (
    <>
      <Header style={{ ...s.header, ...(showHeaderBorder ? s.headerBorder : null) }}>
        <Button type='primary' href={BRIEF_BUILDER} style={s.headerButton}>
          {t('eventHub.sideBar.header.button', 'Plan a new event')}
        </Button>
      </Header>
      <div ref={headerRef}>
        <Menu
          onSelect={({ key }) =>
            setSelectedEventListItem(
              activeEvents.concat(inactiveEvents).find((event) => event.UUID === key)
            )
          }
          selectedKeys={selectedEventListItem ? [selectedEventListItem.UUID] : []}
          mode='inline'
          style={{ lineHeight: 'unset' }}>
          {activeEvents?.length > 0 ? (
            activeEvents.map((event, index) => (
              <>
                <Menu.Item
                  style={{
                    ...s.menuItem.parent,
                    ...(event.UUID === selectedEventListItem?.UUID ? s.menuItem.selected : null),
                  }}
                  key={event.UUID}>
                  <EventMenuItem event={event} isArchived={false} />
                </Menu.Item>
                {index < activeEvents.length - 1 && <Menu.Divider style={s.menuItem.divider} />}
              </>
            ))
          ) : activeLoading || activeError ? (
            <div style={s.activeSpinner}>
              <LoadError load={activeLoading} error={activeError} />
            </div>
          ) : (
            <div style={s.noEvents.root}>
              <Paragraph style={s.noEvents.text}>
                {t(
                  'eventHub.sideBar.noActiveEvents',
                  'This is where your current events will live. Get started by '
                )}
                <Link href={BRIEF_BUILDER}>{t('eventHub.sideBar.planFirstEvent', 'planning your first event')}</Link>
              </Paragraph>
            </div>
          )}
          <Menu.Divider style={s.menuItem.divider} />
          <div style={s.archive.root}>
            <Text>{t('eventHub.sideBar.showArchive', 'Archive')}</Text>
            {showArchive ? (
              <MinusSquareOutlined style={s.archive.icon} onClick={handleToggleShowArchive} />
            ) : (
              <PlusSquareOutlined style={s.archive.icon} onClick={handleToggleShowArchive} />
            )}
          </div>
          {showArchive &&
            (inactiveEvents?.length > 0 ? (
              inactiveEvents?.map((event, index) => (
                <>
                  <Menu.Item
                    style={{
                      ...s.menuItem.parent,
                      ...(event.UUID === selectedEventListItem?.UUID ? s.menuItem.selected : null),
                    }}
                    key={event.UUID}>
                    <EventMenuItem event={event} isArchived={true} />
                  </Menu.Item>
                  {index < inactiveEvents.length - 1 && <Menu.Divider style={s.menuItem.divider} />}
                </>
              ))
            ) : inactiveLoading || inactiveError ? (
              <div style={s.archiveSpinner}>
                <LoadError load={inactiveLoading} error={inactiveError} />
              </div>
            ) : (
              <div style={s.noEvents.root}>
                <Paragraph style={s.noEvents.text}>
                  {t(
                    'eventHub.sideBar.emptyArchive',
                    'This is where you can find events that have already happened, or that you’ve cancelled.'
                  )}
                </Paragraph>
              </div>
            ))}
        </Menu>
      </div>
    </>
  )
}
